import { useEffect, useState, type MutableRefObject } from "react";
import { useTranslation } from "react-i18next";
import {
  UPLOAD_POLICIES,
  UPLOAD_PURPOSE_MARKETPLACE_PARTNER_PREFLIGHT,
  validateUploadMeta,
} from "@nexus/contracts";
import { Button, Icon } from "@nexus/ui";
import { toast } from "sonner";

import { useAuth } from "@/features/auth/hooks/useAuth";
import { runPartnerPreflightUploadFlow } from "@/features/marketplace/api/partner-preflight-upload.api";
import { getMarketplacePartnerPreflightUploadConstraints } from "@/features/marketplace/lib/marketplace-partner-preflight-upload-constraints";
import { resolvePartnerPreflightContentType } from "@/features/marketplace/lib/resolve-partner-preflight-content-type";
import { PartnerVideoConfirmDialog } from "@/features/marketplace/partner/components/PartnerVideoConfirmDialog";
import {
  PartnerPreflightUploadProgress,
  type PartnerPreflightSubmitPhase,
} from "@/features/marketplace/partner/components/PartnerPreflightUploadProgress";
import {
  isPartnerUploadCancellablePhase,
  type PartnerPreflightSuccessState,
} from "@/features/marketplace/partner/components/PartnerVideoUploadSlot";
import {
  partnerVideoSlotEmpty,
  reviewIdAfterPartnerSubmit,
} from "@/features/marketplace/partner/lib/partner-videos";
import type {
  MarketplaceOrder,
  OrderLineVideo,
  OrderScriptRow,
  PartnerOrder,
  PartnerPreflightIntent,
} from "@/features/marketplace/types/marketplace.types";
import { UploadDropzone } from "@/features/preflight/components/UploadDropzone/UploadDropzone";
import { getUserFacingApiMessage } from "@/lib/api-client";
import { usePartnerVoiceoverCheck, useUploadPartnerPreflight } from "@/lib/queries";

const PARTNER_VIDEO_POLICY =
  UPLOAD_POLICIES[UPLOAD_PURPOSE_MARKETPLACE_PARTNER_PREFLIGHT];
const PARTNER_PREFLIGHT_UPLOAD =
  getMarketplacePartnerPreflightUploadConstraints();

type PartnerPreflightRowPhase = PartnerPreflightSubmitPhase | "idle";

/**
 * A legacy line keeps video 1 on the line row, so it has no deliverable id.
 * No intent sends the upload down the server's line-level path, as on `main`.
 */
function resolveSlotIntent(video: OrderLineVideo): PartnerPreflightIntent | undefined {
  if (!video.id) return undefined;
  return { deliverableId: video.id };
}

export function PartnerVideoSlotUpload({
  order,
  script,
  video,
  title,
  abortRef,
  isLocked = false,
  onBusyChange,
  onUploadSuccess,
}: {
  order: PartnerOrder;
  script: OrderScriptRow;
  video: OrderLineVideo;
  title: string;
  abortRef: MutableRefObject<AbortController | null>;
  isLocked?: boolean;
  onBusyChange?: (busy: boolean) => void;
  onUploadSuccess: (state: PartnerPreflightSuccessState) => void;
}) {
  const { t } = useTranslation();
  const activeTenantId = useAuth((state) => state.activeTenantId);
  const uploadPreflight = useUploadPartnerPreflight();
  const voiceoverCheck = usePartnerVoiceoverCheck();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [pendingUploadId, setPendingUploadId] = useState<string | null>(null);
  const [fileRejection, setFileRejection] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadPhase, setUploadPhase] = useState<PartnerPreflightRowPhase>("idle");
  const isSubmitting = uploading || uploadPreflight.isPending;
  const isEmpty = partnerVideoSlotEmpty(video);
  const intent = resolveSlotIntent(video);

  const isBusy = isSubmitting || pendingUploadId !== null;
  useEffect(() => {
    onBusyChange?.(isBusy);
  }, [isBusy, onBusyChange]);

  const completeSubmit = (
    updatedOrder: MarketplaceOrder,
    submitIntent?: PartnerPreflightIntent,
  ) => {
    if (useAuth.getState().activeTenantId !== activeTenantId) return;
    const updatedScript = updatedOrder.scripts.find(
      (candidate) => candidate.id === script.id,
    );
    const scriptReviewId = reviewIdAfterPartnerSubmit(updatedScript, submitIntent);
    if (scriptReviewId && updatedScript) {
      onUploadSuccess({
        scriptReviewId,
        scriptTitle: title,
        clientName: order.tenant.name,
        submissionStatus: updatedScript.fulfillmentStatus,
      });
    }
    setSelectedFile(null);
  };

  const handleFiles = (files: FileList | File[]) => {
    const nextFile = files[0];
    if (!nextFile) return;
    const contentType = resolvePartnerPreflightContentType(
      nextFile,
      PARTNER_VIDEO_POLICY,
    );
    const validation = validateUploadMeta(
      { contentType, sizeBytes: nextFile.size },
      PARTNER_VIDEO_POLICY,
    );
    if (!validation.ok) {
      setSelectedFile(null);
      setFileRejection(
        t("marketplace.partner.fileRejected", {
          fileName: nextFile.name,
          reason: validation.reason.replace(/_/g, " ").toLowerCase(),
          defaultValue: "{{fileName}} — {{reason}}",
        }),
      );
      return;
    }
    setSelectedFile(nextFile);
    setFileRejection(null);
  };

  const resetUploadState = () => {
    abortRef.current = null;
    setUploading(false);
    setUploadProgress(0);
    setUploadPhase("idle");
  };

  const handleSubmit = async () => {
    if (!selectedFile || !activeTenantId) return;
    const controller = new AbortController();
    abortRef.current = controller;
    setUploading(true);
    setUploadProgress(0);
    setUploadPhase("preparing");
    try {
      const upload = await runPartnerPreflightUploadFlow({
        tenantId: activeTenantId,
        file: selectedFile,
        signal: controller.signal,
        onProgress: (percent) => {
          setUploadProgress(percent);
          if (percent >= 100) setUploadPhase("confirming");
        },
        onPhase: setUploadPhase,
      });
      if (controller.signal.aborted) return;
      resetUploadState();
      setPendingUploadId(upload.uploadId);
      voiceoverCheck.mutate({
        partnerTenantId: activeTenantId,
        orderId: order.id,
        orderScriptId: script.id,
        uploadId: upload.uploadId,
      });
    } catch (error) {
      if (controller.signal.aborted) return;
      if (!(error instanceof DOMException && error.name === "AbortError")) {
        toast.error(
          getUserFacingApiMessage(error) ||
            t(
              "marketplace.partner.preflightUploadError",
              "Couldn't submit preflight",
            ),
        );
      }
      resetUploadState();
    }
  };

  const closeConfirm = () => {
    setPendingUploadId(null);
    voiceoverCheck.reset();
  };

  const handleConfirmUpload = () => {
    if (!pendingUploadId || !activeTenantId) return;
    const uploadId = pendingUploadId;
    closeConfirm();
    setUploadPhase("submitting");
    uploadPreflight.mutate(
      {
        partnerTenantId: activeTenantId,
        orderId: order.id,
        orderScriptId: script.id,
        uploadId,
        intent,
      },
      {
        onSuccess: (updatedOrder) => completeSubmit(updatedOrder, intent),
        onSettled: resetUploadState,
      },
    );
  };

  return (
    <div className="mt-2 flex flex-col gap-2 border-t border-border/60 pt-2">
      <UploadDropzone
        onFiles={handleFiles}
        accept={PARTNER_PREFLIGHT_UPLOAD.accept}
        multiple={false}
        disabled={isSubmitting || isLocked}
        browseTrigger="button"
        className="py-4"
        titleText={t(
          "marketplace.partner.dropzoneTitle",
          "Drag, drop or paste video",
        )}
        constraintsText={t("marketplace.partner.dropzoneConstraints", {
          formats: PARTNER_PREFLIGHT_UPLOAD.formats,
          videoMax: PARTNER_PREFLIGHT_UPLOAD.videoMax,
          defaultValue: "{{formats}} · Videos up to {{videoMax}}",
        })}
        buttonLabel={
          selectedFile
            ? t("marketplace.partner.dropzoneReplace", "Replace file")
            : t("marketplace.partner.dropzoneBrowse", "Browse files")
        }
      />
      {fileRejection ? (
        <p className="flex items-center gap-1.5 text-xs text-error">
          <Icon name="alert-circle" size="xs" color="inherit" aria-hidden />
          {fileRejection}
        </p>
      ) : null}
      {selectedFile && !isSubmitting ? (
        <div className="flex min-h-11 items-center justify-between gap-3 rounded-md bg-muted/40 px-3 py-2">
          <span className="min-w-0 truncate text-xs text-foreground">
            {selectedFile.name}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setSelectedFile(null)}
          >
            {t("marketplace.partner.upload.remove", "Remove")}
          </Button>
        </div>
      ) : null}
      {isSubmitting && uploadPhase !== "idle" ? (
        <PartnerPreflightUploadProgress
          phase={uploadPhase}
          progress={uploadProgress}
          fileName={selectedFile?.name}
        />
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          disabled={!selectedFile || isSubmitting || isLocked}
          onClick={() => void handleSubmit()}
        >
          {isSubmitting
            ? t("marketplace.partner.preflightUploading", "Submitting…")
            : t(
                "marketplace.partner.preflightUpload",
                "Submit work for review",
              )}
        </Button>
        {isSubmitting && isPartnerUploadCancellablePhase(uploadPhase) ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              abortRef.current?.abort();
              resetUploadState();
            }}
          >
            {t("marketplace.partner.upload.cancel", "Cancel upload")}
          </Button>
        ) : null}
      </div>
      <PartnerVideoConfirmDialog
        open={pendingUploadId !== null}
        file={selectedFile}
        scriptTitle={title}
        isCheckingVoiceover={voiceoverCheck.isPending}
        voiceoverMismatch={voiceoverCheck.data ?? null}
        onConfirm={handleConfirmUpload}
        onCancel={closeConfirm}
      />
    </div>
  );
}
